import { Inject, Injectable } from '@angular/core';
import { ROUTER_CONFIGURATION, Router } from '@angular/router';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { InvitationApi } from '@assetaccountant/api/services/invitation.api';
import { OrganisationV2Api } from '@assetaccountant/api/services/organisation-v2.api';
import { environment } from 'environments/environment';
import { Observable, from, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { OrganisationOnboardingModel } from '../models/onboarding/onboarding-model';
import { ClientSwitchService } from '../services/client-switch.service';
import { TrackingService } from '../services/tracking.service';
import {
  TypedActivatedRouteSnapshot,
  navigateToRouteWithReplacedParameters,
} from '../utils/route-utils';
import { RegisterResolver } from './register.resolver';

export interface OrganisationParams {
  organisationId: string;
}

export interface OrganisationData {
  organisation: OrganisationResolverResult;
}

export class OrganisationResolverResult {
  activeOrganisation: OrganisationDto;
  activeOrganisationLogo?: string;
  activeOrganisationOnboarding?: OrganisationOnboardingModel;
}

// Todo: Fetch permissions from API and store them in storage
@Injectable({
  providedIn: 'root',
})
export class OrganisationResolver {
  private static _activeOrganisation: Observable<OrganisationDto> | undefined;
  private static orgId: number;
  private static orgLogo?: string;

  private canRetry = true;

  isInitialLoad = true;

  public static clearCache() {
    OrganisationResolver._activeOrganisation = undefined;
    OrganisationResolver.orgId = 0;
    OrganisationResolver.orgLogo = undefined;
  }

  private organisation(routeOrgId: number): Observable<OrganisationDto | undefined> {
    if (routeOrgId && OrganisationResolver.orgId !== routeOrgId) {
      setTimeout(() => {
        OrganisationResolver.clearCache();
        RegisterResolver.clearCache();
      }, 60 * 1000);

      OrganisationResolver._activeOrganisation = this.organisationApi
        .getOrganisation(routeOrgId)
        .pipe(map(({ organisation }) => organisation))
        .pipe(shareReplay());
      OrganisationResolver.orgId = routeOrgId;
      OrganisationResolver.orgLogo = undefined;
    }
    if (!OrganisationResolver._activeOrganisation) {
      return of(undefined);
    }

    return OrganisationResolver._activeOrganisation;
  }

  constructor(
    private router: Router,
    @Inject(ROUTER_CONFIGURATION) private routerConfig: { enableTracing: boolean },
    private trackingService: TrackingService,
    private organisationApi: OrganisationV2Api,
    private invitationApi: InvitationApi,
    private clientSwitch: ClientSwitchService,
  ) {}

  resolve(
    route: TypedActivatedRouteSnapshot<OrganisationParams>,
  ): Observable<OrganisationResolverResult> {
    // The platform nav bar's app switcher links here as /?ucid=..., which redirects to
    // organisations/-. Only consulted when the path names no organisation, so a real
    // organisation id in the route always wins.
    const ucid = route.queryParamMap.get('ucid');
    if (ucid && !+(route.params.organisationId || '').split('-')[0]) {
      return this.resolveByUcid(ucid, route);
    }

    return this.resolveFromRoute(route);
  }

  private resolveByUcid(
    ucid: string,
    route: TypedActivatedRouteSnapshot<OrganisationParams>,
  ): Observable<OrganisationResolverResult> {
    return from(this.clientSwitch.findOrganisation(ucid)).pipe(
      map(match => {
        this.router.navigate(match ? ['/organisations', match.organisationId] : ['/unauthorized']);
        return {} as OrganisationResolverResult;
      }),
      // The lookup failing says nothing about their access, so carry on as if there were no ucid.
      catchError(() => this.resolveFromRoute(route)),
    );
  }

  private resolveFromRoute(
    route: TypedActivatedRouteSnapshot<OrganisationParams>,
  ): Observable<OrganisationResolverResult> {
    const organisationPath = route.params.organisationId || '';
    let organisationId = +organisationPath.split('-')[0];
    let _redirecting = this.isInitialLoad;

    // Only redirect if an organisationId hasn't been set (=== '-')
    if (organisationPath === '-') {
      // Try to get organisationId from session
      organisationId = +(sessionStorage.getItem('organisationId') || '');
      _redirecting = true;
    }

    if (!organisationId) {
      // Else try to get organisationId from local storage
      organisationId = +(localStorage.getItem('organisationId') || '');
      _redirecting = true;
    }

    if (!organisationId) {
      _redirecting = true;
      // If no org ID has been found, then get the list of orgs and default to the first
      return this.getFirstOrganisationFromList(route);
    }

    if (organisationId !== OrganisationResolver.orgId) {
      // If the org ID has changed, then it is a redirect
      _redirecting = true;
    }

    // Else fetch the organisation for the org ID we have found
    return this.handleOrganisation(organisationId, _redirecting, route).pipe(
      map(organisation => {
        if (organisation) {
          return organisation as OrganisationResolverResult;
        } else {
          if (this.routerConfig.enableTracing) {
            console.warn('Organisation Resolver: no organisation found', organisation);
          }

          this.router.navigateByUrl('/');
          return {} as OrganisationResolverResult;
        }
      }),
    );
  }

  getLogo(organisationId: number) {
    if (OrganisationResolver.orgLogo) {
      return of(OrganisationResolver.orgLogo);
    }

    return this.organisationApi.getLogo(organisationId).pipe(
      switchMap(blob =>
        blob
          ? new Observable<string>(subscriber => {
              const reader = new FileReader();
              reader.onloadend = () => {
                subscriber.next(reader.result as string);
                subscriber.complete();
              };

              reader.readAsDataURL(blob);
            })
          : of(undefined),
      ),
      tap(logo => (OrganisationResolver.orgLogo = logo)),
      catchError(err => {
        environment.warn(err);
        return of(undefined);
      }),
    );
  }

  private getFirstOrganisationFromList(
    route: TypedActivatedRouteSnapshot<OrganisationParams>,
  ): Observable<OrganisationResolverResult> {
    return this.organisationApi.listOrganisations2().pipe(
      map(({ result }) => result),
      switchMap(organisations => {
        const organisationId = organisations[0]?.organisationId;

        if (organisationId && this.organisation !== undefined) {
          return this.handleOrganisation(organisationId, true, route).pipe(
            map(organisation => {
              if (organisation) {
                return organisation as OrganisationResolverResult;
              } else {
                if (this.routerConfig.enableTracing) {
                  console.warn('Organisation Resolver: no organisation found', organisation);
                }

                this.router.navigateByUrl('/');
                return {} as OrganisationResolverResult;
              }
            }),
          );
        } else {
          // No organisation so check if there is a pending invite
          return this.invitationApi.getCurrentUserPendingInvitations().pipe(
            map(({ result }) => {
              if (result?.invitationId) {
                // Accept the pending invite
                this.router.navigateByUrl(`/invitations/${result.invitationId}/accept`);
              } else {
                // Nothing here for them, and creating an org is not an option any more.
                this.router.navigateByUrl('/unauthorized');
              }
              return {} as OrganisationResolverResult;
            }),
          );
        }
      }),
    );
  }

  private handleOrganisation(
    orgId: number,
    redirecting: boolean,
    route: TypedActivatedRouteSnapshot<OrganisationParams>,
  ): Observable<OrganisationResolverResult> {
    return this.organisation(orgId).pipe(
      map(organisation => {
        if (organisation) {
          if (redirecting) {
            if (this.routerConfig.enableTracing) {
              console.log('Organisation Resolver: redirecting', organisation);
            }
            sessionStorage.setItem('organisationId', orgId.toString());
            localStorage.setItem('organisationId', orgId.toString());
            this.organisationApi.updateLastAccessed(organisation.organisationId).subscribe();

            navigateToRouteWithReplacedParameters(
              this.router,
              {
                organisationId: organisation.organisationId,
              },
              route,
            );
          }

          if (this.isInitialLoad) {
            this.isInitialLoad = false;
            this.trackingService.initaliseUserTracking();
            this.trackingService.updateIdentity(organisation);
          }

          return {
            activeOrganisation: organisation,
          } as OrganisationResolverResult;
        } else {
          throw new Error('Organisation not found');
        }
      }),
      switchMap(orgData => {
        if (orgData?.activeOrganisation) {
          this.getLogo(orgData?.activeOrganisation.organisationId).pipe(
            map(activeOrganisationLogo => ({
              ...orgData,
              activeOrganisationLogo,
            })),
          );
        }

        return of(orgData);
      }),
      catchError(error => {
        console.warn('Organisation not found for this ID:', orgId, error);
        // Presume we have a bad organisation ID from storage
        if (this.canRetry) {
          // Prevent looping if ID is still bad
          this.canRetry = false;
          return this.getFirstOrganisationFromList(route);
        } else {
          throw new Error('Organisation not found');
        }
      }),
    );
  }
}
